export interface FlamioConfig {
  projectKey: string;
  endpoint: string;
  /**
   * Optional site/release version (e.g. '1.4.2'). Attached to every session so
   * the dashboard can filter sitemap & analytics per version and measure
   * release impact. Set this at deploy time to track route/UX changes.
   */
  version?: string;
  sampleRate?: number;
  /**
   * Max sessions recorded at once for this project (default 15, hard-capped at
   * 100 server-side). Beyond it new visitors are not recorded, protecting the
   * ingest servers under a traffic spike.
   */
  maxConcurrentSessions?: number;
  consent?: 'granted' | 'denied' | 'wait';
  maskTextContent?: boolean;
  /**
   * Client-side masking + URL gating (see README → Privacy). maskSelectors /
   * blockSelectors blank / omit matching elements; urlAllowlist / urlBlocklist
   * gate which pages record (blocklist wins; `*` is the only wildcard).
   */
  maskSelectors?: string[];
  blockSelectors?: string[];
  urlAllowlist?: string[];
  urlBlocklist?: string[];
  checkoutEveryNms?: number;
  batchInterval?: number;
  batchMaxSize?: number;
  retryDelayMs?: number;
  debug?: boolean;
}

export interface RecordingPrivacyRules {
  maskSelectors: string[];
  blockSelectors: string[];
  urlAllowlist: string[];
  urlBlocklist: string[];
}

// Why a tab session ended. Mirrors SESSION_EXIT_REASON in @flamioai/shared/types
// (the server adds 'unknown' for crash/cron finalization — the SDK never sends it).
// Named here so the wire literals aren't inlined at call sites; keep these two
// values in sync with the shared const.
export const SESSION_END_REASON = {
  Idle: 'idle',
  Exit: 'exit',
} as const;

export type SessionEndReason =
  (typeof SESSION_END_REASON)[keyof typeof SESSION_END_REASON];

export interface BatchPayload {
  sessionId: string;
  batchIndex: number;
  visitorId: string;
  visitId?: string;
  eventCount: number;
  // True when this batch carries a meaningful user interaction — lets the
  // server advance session liveness without decoding the batch (counterpart:
  // ingestion.controller.ts BatchMetadata.meaningful).
  meaningful: boolean;
  deviceInfo?: DeviceInfo;
  entryUrl?: string;
  referrer?: string;
  utmParams?: Record<string, string>;
  version?: string;
  sessionEnd?: boolean;
  exitReason?: SessionEndReason;
}

export interface DeviceInfo {
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  language: string;
  platform: string;
}

export interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: number;
  source?: string;
  stack?: string;
}

export interface NetworkEntry {
  method: string;
  url: string;
  status: number;
  duration: number;
  timestamp: number;
  error?: string;
  slow?: boolean;
  // Browser Resource Timing extras (when available): time-to-first-byte, bytes
  // over the wire, and how the request was initiated (fetch / xmlhttprequest).
  ttfb?: number;
  transferSize?: number;
  initiatorType?: string;
}

export interface VitalEntry {
  name: 'LCP' | 'INP' | 'CLS';
  value: number;
  timestamp: number;
}

export interface Uploader {
  addEvents(events: unknown[]): void;
  /** `final: true` marks the page-exit flush — the only one allowed to use keepalive. */
  flush(final?: boolean): Promise<void>;
  /** Flush and tag the batch as the session's end, with why it ended. */
  endSession(reason: SessionEndReason, useKeepalive?: boolean): Promise<void>;
  /** Drop events until the next `rotateSession` (session ended, awaiting a new one). */
  suspend(): void;
  /** Begin a fresh tab-session (new id + batch counter) and stop dropping events. */
  rotateSession(): void;
  setVisitId(visitId: string): void;
  setIdentity(userId: string, traits?: Record<string, unknown>): void;
  destroy(): Promise<void>;
}
